import { useState, useEffect } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { useLocation, Outlet } from 'react-router'
import { toast } from 'react-toastify'
import { useSessionStorage, useLogout } from '@/hooks'
import { ConfirmDialog } from '@/ui/Dialog/ConfirmDialog'
import { LoginPage } from '@/ux/LoginPage'

export const ProtectedRoutes = () => {
  const [token] = useSessionStorage('token', '')
  const location = useLocation()
  const { pathname, search } = location

  const redirectPath = JSON.stringify({
    pathname,
    search: { ...Object.fromEntries(new URLSearchParams(search)), authStatus: 'login-success' },
  })

  if (!token) return <LoginPage redirectPath={redirectPath} />

  return (
    <AutoLogout>
      <Outlet />
    </AutoLogout>
  )
}

const AutoLogout = ({ children }: { children: React.ReactNode }) => {
  const timeout = 1000 * 60 * 15 // 15 minutes
  const promptBeforeIdle = 1000 * 60 * 0.5 // 30 seconds

  const [remaining, setRemaining] = useState<number>(timeout)
  const { logoutHandler } = useLogout()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const onPrompt = () => {
    setIsDialogOpen(true)
  }

  const onActive = () => {
    console.log('onActive')
  }

  const onIdle = () => {
    toast.dismiss('prompt/active') // Clear the warning toast if it's still showing
    logoutHandler()
  }

  const { getRemainingTime, activate } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 500,
  })

  const handleStillActive = () => {
    setIsDialogOpen(false)
    activate()
  }

  // Clean up on unmount
  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000))
    }, 500)

    return () => {
      clearInterval(interval)
    }
  })

  return (
    <>
      {children}
      <ConfirmDialog
        title="Are you still here?"
        content={`Logging out in ${remaining} seconds`}
        cancelText="Logout"
        confirmText="I'm still here"
        onCancel={logoutHandler}
        onConfirm={handleStillActive}
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
      />
    </>
  )
}
